import React, { useEffect, useMemo, useState } from 'react';
import { isValidMarcacaoId } from '../config/prospeccaoMarcacao';
import cccApi from '../api/cccApi';
import ComercialManagement from '@crm/components/Comercial/ComercialManagement';
import '@crm/components/Comercial/ComercialManagement.css';

type RetencaoClientSeed = {
  id: string;
  company: string;
  nif?: string;
  contactName?: string;
  phone?: string;
  email?: string;
  googleMapsLink?: string;
};

type MarcacaoSeed = {
  id?: number;
  marcacao_id?: string;
  nome_empresa?: string;
  nome_responsavel?: string;
  morada?: string;
  contacto_cliente?: string;
  email?: string;
  form_data?: Record<string, unknown>;
  [key: string]: unknown;
};

type Props = {
  client: RetencaoClientSeed;
  marcacaoId: string;
  onClose: () => void;
  onSuccess: () => void;
};

const swallowOverlayBackdropPointer = (e: React.MouseEvent) => {
  if (e.target === e.currentTarget) {
    e.stopPropagation();
  }
};

const CccSolicitarPropostaRetencaoClientOverlay: React.FC<Props> = ({
  client,
  marcacaoId,
  onClose,
  onSuccess,
}) => {
  const [seed, setSeed] = useState<Record<string, MarcacaoSeed> | null>(null);
  const resolvedMarcacaoId = useMemo(
    () => (isValidMarcacaoId(marcacaoId) ? marcacaoId.trim() : ''),
    [marcacaoId]
  );

  useEffect(() => {
    if (!resolvedMarcacaoId) return;
    let cancelled = false;

    const fallbackSeed: Record<string, MarcacaoSeed> = {
      [resolvedMarcacaoId]: {
        marcacao_id: resolvedMarcacaoId,
        nome_empresa: client.company,
        nome_responsavel: client.contactName || '',
        contacto_cliente: client.phone || '',
        email: client.email || '',
        morada: client.googleMapsLink || '',
        empresa: client.company,
        responsavel: client.contactName || '',
        contacto: client.phone || '',
      },
    };

    (async () => {
      try {
        const data = await cccApi.getRetencaoClientMarcacao(client.id);
        if (!cancelled && data?.success && data.marcacao) {
          setSeed({ [resolvedMarcacaoId]: data.marcacao as MarcacaoSeed });
          return;
        }
      } catch {
        /* fallback */
      }
      if (!cancelled) setSeed(fallbackSeed);
    })();

    return () => {
      cancelled = true;
    };
  }, [client, resolvedMarcacaoId]);

  if (!resolvedMarcacaoId) {
    return (
      <div className="rec-solicitar-proposta-overlay" role="presentation">
        <div className="rec-solicitar-proposta-loading">
          <p>Marcação inválida — não foi possível abrir Solicitar Proposta.</p>
          <button type="button" className="rec-btn rec-btn-ghost" onClick={onClose}>
            Fechar
          </button>
        </div>
      </div>
    );
  }

  if (!seed) {
    return (
      <div className="rec-solicitar-proposta-overlay" role="presentation">
        <div className="rec-solicitar-proposta-loading">A carregar formulário…</div>
      </div>
    );
  }

  return (
    <div
      className="rec-solicitar-proposta-overlay"
      role="presentation"
      onMouseDown={swallowOverlayBackdropPointer}
    >
      <ComercialManagement
        embeddedProposalOnly
        embeddedProposalMode="solicitar"
        embeddedProposalMarcacaoId={resolvedMarcacaoId}
        embeddedRetencaoClientId={client.id}
        embeddedMarcacaoSeed={seed as Record<string, never>}
        onEmbeddedProposalClose={onClose}
        onEmbeddedProposalSuccess={onSuccess}
      />
    </div>
  );
};

export default CccSolicitarPropostaRetencaoClientOverlay;
